import { useState, type FormEvent } from "react";

import { Button, Card, CardBody, CardHead, EmptyState, Select } from "@/components/common";
import { usePermission } from "@/auth/usePermission";
import { PERM } from "@/auth/permissions";
import { extractErrorMessage } from "@/services/axiosConfig";
import type { OperatorType } from "@/types/contract";
import type { WorkspaceSettings } from "@/types/workspace";

const OPERATOR_OPTIONS: { value: OperatorType; label: string }[] = [
  { value: "tributo_devido", label: "Tributo Devido" },
  { value: "organization", label: "Organização parceira" },
  { value: "workspace", label: "Workspace próprio" },
];
import { useUpdateWorkspaceSettings, useWorkspaceSettings } from "@/hooks/useWorkspaces";
import "./WorkspaceSettingsTab.css";

export function WorkspaceSettingsTab({ workspaceId }: { workspaceId: number }) {
  const { data: settings, isLoading } = useWorkspaceSettings(workspaceId);
  if (isLoading) return <EmptyState icon="settings" title="Carregando…" />;
  if (!settings) return <EmptyState icon="settings" title="Sem configurações" />;
  return <SettingsForm workspaceId={workspaceId} settings={settings} />;
}

function SettingsForm({ workspaceId, settings }: { workspaceId: number; settings: WorkspaceSettings }) {
  const canEdit = usePermission(PERM.workspacesWrite);
  const mut = useUpdateWorkspaceSettings(workspaceId);
  const [form, setForm] = useState({
    initials: settings.initials,
    display_color: settings.display_color,
    timezone: settings.timezone,
    locale: settings.locale,
    success_fee_default: settings.success_fee_default,
    imposto_indicacao_default: settings.imposto_indicacao_default,
    consultoria_repasse_pct: settings.consultoria_repasse_pct,
    operator_type: settings.operator_type,
    default_td_share_pct: settings.default_td_share_pct ?? "",
  });
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  function field<K extends keyof typeof form>(key: K) {
    return {
      value: form[key],
      disabled: !canEdit,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        setForm((f) => ({ ...f, [key]: e.target.value }));
        setSaved(false);
      },
    };
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await mut.mutateAsync({
        ...form,
        default_td_share_pct: form.default_td_share_pct.trim() || null,
      });
      setSaved(true);
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  }

  return (
    <form className="ws-settings" onSubmit={onSubmit}>
      {error && <div className="form-error">{error}</div>}

      <Card>
        <CardHead title="Identidade" subtitle="Whitelabel exibido no app" />
        <CardBody>
          <div className="form-grid">
            <label className="form-field">
              <span>Iniciais</span>
              <input className="form-input" maxLength={3} {...field("initials")} />
            </label>
            <label className="form-field">
              <span>Cor de exibição</span>
              <input className="form-input" type="color" {...field("display_color")} />
            </label>
            <label className="form-field">
              <span>Fuso horário</span>
              <input className="form-input" {...field("timezone")} />
            </label>
            <label className="form-field">
              <span>Idioma</span>
              <input className="form-input" {...field("locale")} />
            </label>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHead
          title="Padrões de contrato"
          subtitle="Modelo de operação e valores entre 0 e 1 (fração)"
        />
        <CardBody>
          <div className="form-grid">
            <label className="form-field">
              <span>Modelo de operação dos contratos</span>
              <Select<OperatorType>
                value={form.operator_type}
                onChange={(v) => {
                  setForm((f) => ({ ...f, operator_type: v }));
                  setSaved(false);
                }}
                options={OPERATOR_OPTIONS}
                disabled={!canEdit}
              />
            </label>
            <label className="form-field">
              <span>Participação TD padrão</span>
              <input
                className="form-input"
                {...field("default_td_share_pct")}
                placeholder="ex: 0.85 (opcional)"
              />
            </label>
            <label className="form-field">
              <span>Success fee padrão</span>
              <input className="form-input" {...field("success_fee_default")} />
            </label>
            <label className="form-field">
              <span>Imposto indicação padrão</span>
              <input className="form-input" {...field("imposto_indicacao_default")} />
            </label>
            <label className="form-field">
              <span>Repasse consultoria</span>
              <input className="form-input" {...field("consultoria_repasse_pct")} />
            </label>
          </div>
        </CardBody>
      </Card>

      {canEdit && (
        <div className="ws-settings-foot">
          {saved && <span className="td-small">Salvo ✓</span>}
          <Button variant="primary" type="submit" disabled={mut.isPending}>
            {mut.isPending ? "Salvando…" : "Salvar alterações"}
          </Button>
        </div>
      )}
    </form>
  );
}
